import React, { useEffect, useState } from "react";
import { handlePrintTicket, handleShareTicket } from "./handlePrintTicket";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { Printer } from "lucide-react";
import { TICKET_IMG } from "../../utils/Contants";
import { apiCall } from "../../Apis/apiCall";

// === MAPPING: Backend to Frontend ===
const mapLayoutFromBackend = (value) => {
  const map = {
    "image_top_both": "img-top-both",
    "name_top_both": "name-top-both",
    "logo_only": "img-top-logo",
    "name_only": "img-top-name",
    "side_by_side": "side-by-side-both",
  };
  return map[value] || "img-top-both";
};

// Helper to extract layout and content
const getLayout = (value) => {
  if (value.includes("img")) return "img";
  if (value.includes("name-top")) return "name";
  if (value.includes("side")) return "side";
  return "img";
};

const getContent = (value) => {
  if (value.includes("both")) return "both";
  if (value.includes("logo")) return "logo";
  if (value.includes("name")) return "name";
  return "both";
};

/**
 * Generate QR Code as base64 data URL
 */
const generateQRCodeDataURL = async (data) => {
  try {
    // const qrData = `Ticket:${data.ticket_number || 'N/A'}|Plate:${data.vehicle_license_plate || 'N/A'}|Time:${data.parked_time || ''}`;
   const qrData = data.ticket_number;
    const size = 150;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(qrData)}&format=png`;
    
    // Fetch and convert to base64
    const response = await fetch(qrUrl);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("QR Code generation error:", error);
    return null;
  }
};

const TicketComponent = ({ data, fieldName }) => {
  const [loading, setLoading] = useState(false);
  const [ticketImagePreview, setTicketImagePreview] = useState("");
  const [qrCodeDataURL, setQrCodeDataURL] = useState(null);
  const [templateData, setTemplateData] = useState({
    ticket_logo_url: "",
    ticket_company_name: "",
    ticket_footer_text: "",
    ticket_layout: "img-top-both", // frontend format
  });
  const summaryData = data || {};

  useEffect(() => {
    fetchTemplateData();
    generateQRCode();
  }, []);

  const generateQRCode = async () => {
    const qrUrl = await generateQRCodeDataURL(summaryData);
    setQrCodeDataURL(qrUrl);
  };

  const fetchTemplateData = async () => {
    setLoading(true);
    try {
      const response = await apiCall("get", "/get-receipt-ticket-temp");
      if (response.data.success) {
        const data = response.data.data;
        console.log("Fetched Data:", data);

        // Ensure layout is properly mapped from backend
        setTemplateData({
          ticket_logo_url: data.ticket_logo_url || "",
          ticket_company_name: data.ticket_company_name || "",
          ticket_footer_text: data.ticket_footer_text || "",
          ticket_layout: mapLayoutFromBackend(data.ticket_layout), // Convert to frontend
        });

        // Build full URLs for preview
        if (data.ticket_logo_url) {
          const ticketUrl = `${TICKET_IMG}/${data.ticket_logo_url}`;
          console.log("Ticket Logo URL:", ticketUrl);
          setTicketImagePreview(ticketUrl);
        }
      }
    } catch (error) {
      console.error("Error fetching template data:", error);
      setTicketImagePreview("");
    } finally {
      setLoading(false);
    }
  };

  // Header component based on layout - EXACT SAME LOGIC as handlePrintTicket
  const renderHeader = () => {
    const layoutValue = templateData.ticket_layout;
    const layout = getLayout(layoutValue);
    const content = getContent(layoutValue);

    const logoUrl = ticketImagePreview;
    const name = templateData.ticket_company_name || "PARK EASE VALET";

    const showLogo = logoUrl && content !== "name";
    const showName = name && content !== "logo";
    const showQR = qrCodeDataURL;

    if (!showLogo && !showName) return null;

    // Layout: img (image on top)
    if (layout === "img") {
      return (
        <div className="mb-3" style={{ display: "table", width: "100%" }}>
          <div style={{ display: "table-cell", verticalAlign: "top", width: showQR ? "70%" : "100%" }}>
            {showLogo && (
              <img
                src={logoUrl}
                alt="Ticket Logo"
                style={{ 
                  height: "45px", 
                  maxWidth: "100%",
                  objectFit: "contain",
                  display: "block",
                  margin: "0 0 6px 0"
                }}
                onError={(e) => {
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjYwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPklNRzwvdGV4dD48L3N2Zz4=";
                }}
              />
            )}
            {showName && (
              <h6 className="mt-2 mb-0 text-neutral-900 font-weight-bold" style={{ fontSize: "13px" }}>
                {name}
              </h6>
            )}
          </div>
          {showQR && (
            <div style={{ display: "table-cell", verticalAlign: "top", width: "30%", textAlign: "right" }}>
              <img
                src={qrCodeDataURL}
                alt="QR Code"
                style={{
                  width: "55px",
                  height: "55px",
                  display: "inline-block",
                  padding: "2px",
                  background: "#ffffff"
                }}
              />
            </div>
          )}
        </div>
      );
    }

    // Layout: name (name on top)
    if (layout === "name") {
      return (
        <div className="mb-3" style={{ display: "table", width: "100%" }}>
          <div style={{ display: "table-cell", verticalAlign: "top", width: showQR ? "70%" : "100%" }}>
            {showName && (
              <h6 className="mb-2 text-neutral-900 font-weight-bold" style={{ fontSize: "13px" }}>
                {name}
              </h6>
            )}
            {showLogo && (
              <img
                src={logoUrl}
                alt="Ticket Logo"
                style={{ 
                  height: "45px", 
                  maxWidth: "100%",
                  objectFit: "contain",
                  display: "block"
                }}
                onError={(e) => {
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjYwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPklNRzwvdGV4dD48L3N2Zz4=";
                }}
              />
            )}
          </div>
          {showQR && (
            <div style={{ display: "table-cell", verticalAlign: "top", width: "30%", textAlign: "right" }}>
              <img
                src={qrCodeDataURL}
                alt="QR Code"
                style={{
                  width: "55px",
                  height: "55px",
                  display: "inline-block",
                  padding: "2px",
                  background: "#ffffff"
                }}
              />
            </div>
          )}
        </div>
      );
    }

    // Layout: side (side-by-side)
    if (layout === "side") {
      return (
        <div className="mb-3" style={{ display: "table", width: "100%" }}>
          <div style={{ display: "table-cell", verticalAlign: "top", width: showQR ? "70%" : "100%" }}>
            <div className="d-flex align-items-center gap-2">
              {showLogo && (
                <img
                  src={logoUrl}
                  alt="Ticket Logo"
                  style={{ height: "40px", maxWidth: "45%", objectFit: "contain" }}
                  onError={(e) => {
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjYwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPklNRzwvdGV4dD48L3N2Zz4=";
                  }}
                />
              )}
              {showName && (
                <h6 className="mb-0 text-neutral-900 font-weight-bold" style={{ fontSize: "12px" }}>
                  {name}
                </h6>
              )}
            </div>
          </div>
          {showQR && (
            <div style={{ display: "table-cell", verticalAlign: "top", width: "30%", textAlign: "right" }}>
              <img
                src={qrCodeDataURL}
                alt="QR Code"
                style={{
                  width: "55px",
                  height: "55px",
                  display: "inline-block",
                  padding: "2px",
                  background: "#ffffff"
                }}
              />
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="ticket-summary-container d-flex justify-content-center flex-column align-items-center">
      <div
        className={`ticket-summary-mockup-${fieldName} position-relative overflow-hidden p-2`}
        style={{
          fontFamily: "var(--default-font)",
          fontSize: "var(--font-sm)",
          background: "var(--white)",
          boxShadow: "var(--shadow-4)",
          borderRadius: "var(--rounded-8)",
          border: "1px solid var(--neutral-300)",
          marginBottom: "var(--size-16)",
        }}
      >
        {/* Dynamic Header with QR Code based on layout */}
        {renderHeader()}

        <div
          className="p-3 pb-2"
          style={{ borderBottom: `1px dashed var(--neutral-300)` }}
        >
          {[
            {
              label: "Ticket Number #:",
              value: summaryData.ticket_number || "-",
            },
            {
              label: "Parking Ticket #:",
              value: summaryData.parking_ticket || "-",
            },
            {
              label: "Customer Name:",
              value: summaryData.customer_name || "-",
            },
            {
              label: "Customer Phone:",
              value: summaryData.customer_phone || "-",
            },
            {
              label: "Entry Time:",
              value: summaryData.parked_time
                ? new Date(summaryData.parked_time).toLocaleString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "-",
            },
            ...(summaryData.entry_gate && summaryData.entry_gate.trim() !== ""
              ? [{ label: "Entry Gate:", value: summaryData.entry_gate }]
              : []),
          ].map((item, index) => (
            <div
              key={index}
              className="d-flex justify-content-between mb-1"
              style={{ color: "var(--text-secondary-light)" }}
            >
              <span>{item.label}</span>
              <span
                className="fw-medium"
                style={{ color: "var(--text-primary-light)" }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>

        <div
          className="p-3 pb-2"
          style={{ borderBottom: `2px dashed var(--neutral-300)` }}
        >
          <div
            className="fw-bold mb-2"
            style={{
              color: "var(--neutral-700)",
              fontSize: "var(--font-md)",
            }}
          >
            Vehicle Details
          </div>
          {[
            {
              label: "Plate Number:",
              value: summaryData.vehicle_license_plate || "-",
            },
            {
              label: "Vehicle Type:",
              value: summaryData.vehicle_type || "-",
            },
            {
              label: "Vehicle Make:",
              value: summaryData.vehicle_make || "-",
            },
            {
              label: "Vehicle Model:",
              value: summaryData.vehicle_model || "-",
            },
            {
              label: "Vehicle Color:",
              value: summaryData.vehicle_color || "-",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="d-flex justify-content-between mb-1"
              style={{ color: "var(--text-secondary-light)" }}
            >
              <span>{item.label}</span>
              <span
                className="fw-medium"
                style={{ color: "var(--text-primary-light)" }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>

        {summaryData.vehicle_damage_notes && (
          <div
            className="p-3 pb-2"
            style={{ borderBottom: `1px dashed var(--neutral-300)` }}
          >
            <div
              className="fw-bold mb-2"
              style={{
                color: "var(--neutral-700)",
                fontSize: "var(--font-md)",
              }}
            >
              Notes
            </div>
            <div
              style={{
                fontSize: "var(--font-xs)",
                color: "var(--text-secondary-light)",
                lineHeight: "1.5",
              }}
            >
              {summaryData.vehicle_damage_notes}
            </div>
          </div>
        )}

        {summaryData.parking_fee && (
          <div
            className="p-3 pb-2"
            style={{ borderBottom: `1px dashed var(--neutral-300)` }}
          >
            <div
              className="fw-bold mb-2"
              style={{
                color: "var(--neutral-700)",
                fontSize: "var(--font-md)",
              }}
            >
              Parking Fee
            </div>
            <div
              className="d-flex justify-content-between mb-1"
              style={{ color: "var(--text-secondary-light)" }}
            >
              <span>Amount:</span>
              <span
                className="fw-medium"
                style={{ color: "var(--text-primary-light)" }}
              >
                {summaryData.parking_fee || "-"}
              </span>
            </div>
          </div>
        )}

        {/* Footer with custom text from API */}
        <div className="d-flex flex-column align-items-center justify-content-center text-center py-3 border-top">
          {templateData.ticket_footer_text && (
            <div 
              className="mb-2 font-weight-medium"
              style={{ color: "var(--neutral-700)", fontSize: "var(--font-sm)" }}
            >
              {templateData.ticket_footer_text}
            </div>
          )}
          <span className="small" style={{ color: "var(--neutral-600)" }}>
            Powered by{" "}
            <span className="fw-semibold text-primary ms-1">PARK EASE</span>
          </span>
        </div>
      </div>

      <div className="d-flex gap-3 mt-3">
        <Button
          variant="outline-primary"
 onClick={() =>
            handleShareTicket(
              summaryData,
              "ticket",
              ticketImagePreview,
              templateData
            )
          }          className="d-flex align-items-center gap-2"
          style={{ minWidth: "120px" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z" />
          </svg>
          Share Ticket
        </Button>
        <Button
          variant="outline-info"
          onClick={() =>
            handlePrintTicket(
              summaryData,
              "ticket",
              ticketImagePreview,
              templateData
            )
          }
          className="d-flex align-items-center gap-2"
        >
          <Printer size={16} /> Print
        </Button>
      </div>
    </div>
  );
};

export default TicketComponent;